/**
 * โหลดข้อมูลผู้ใช้และ settings จาก /api/me ครั้งเดียวตอนเปิดแอป
 *
 * settings มาจาก server เสมอ ไม่ hard-code ฝั่ง client
 * เกณฑ์ GPS กฎเลขทะเบียน และค่าบีบอัดรูปจึงเปลี่ยนได้จากหน้า Settings
 * โดยไม่ต้อง deploy ใหม่
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type SessionResponse } from '../lib/api';

export type SessionState =
  | { status: 'loading' }
  | { status: 'ready'; session: SessionResponse }
  | { status: 'unauthenticated'; message: string }
  | { status: 'forbidden'; message: string }
  | { status: 'error'; message: string };

export function useSession() {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  // ไม่ตั้งสถานะ loading ตรงนี้ เพราะค่าเริ่มต้นเป็น loading อยู่แล้ว
  // การ setState แบบ synchronous ภายใน effect ทำให้เกิด render รอบที่เสียเปล่า
  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const session = await api.session(signal);
      setState({ status: 'ready', session });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (error instanceof ApiError) {
        if (error.status === 401) {
          setState({ status: 'unauthenticated', message: error.message });
          return;
        }
        if (error.status === 403) {
          setState({ status: 'forbidden', message: error.message });
          return;
        }
        setState({ status: 'error', message: error.message });
        return;
      }
      setState({ status: 'error', message: 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้' });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    /*
     * eslint-disable-next-line react-hooks/set-state-in-effect --
     * นี่คือการโหลดข้อมูลตอน mount ซึ่งจำเป็นต้องอยู่ใน effect
     * setState ทุกจุดใน load() เกิดหลัง await แล้วทั้งหมด ไม่ใช่ synchronous
     * แต่ rule ตรวจแบบ transitive จึงมองไม่เห็นความต่างนี้
     * มี AbortController ยกเลิก request เมื่อ component ถูก unmount แล้ว
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return {
    state,
    // reload ถูกเรียกจาก event handler จึงตั้ง loading ตรงนี้ได้โดยไม่กระทบ render loop
    reload: () => {
      setState({ status: 'loading' });
      void load();
    },
  };
}

/** สถานะออนไลน์/ออฟไลน์ ใช้ตัดสินใจว่าจะแสดงแถบเตือนและจะยิง API หรือเข้าคิว */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
